/* Owns every TanStack Query key. Keys are hierarchical arrays so a prefix
   invalidation (`['detections', serverId]`) covers the list, the summary and
   every detail at once. Pages never build a key inline. */

import type { StatsRange } from '@sentinelac/shared'

export type Range = StatsRange

export const qk = {
  me: () => ['me'] as const,
  auditMe: () => ['audit', 'me'] as const,
  apiKeys: () => ['apiKeys'] as const,
  servers: () => ['servers'] as const,
  server: (s: string) => ['server', s] as const,
  install: (s: string) => ['server', s, 'install'] as const,
  health: (s: string) => ['stats', s, 'health'] as const,
  overview: (s: string, r: Range) => ['stats', s, 'overview', r] as const,
  detections: (s: string, f: object) => ['detections', s, 'list', f] as const,
  detection: (s: string, d: string) => ['detections', s, 'detail', d] as const,
  detectionSummary: (s: string, r: Range) => ['detections', s, 'summary', r] as const,
  players: (s: string, f: object) => ['players', s, 'list', f] as const,
  player: (s: string, p: string) => ['players', s, 'detail', p] as const,
  bans: (s: string, f: object) => ['bans', s, 'list', f] as const,
  ban: (s: string, b: string) => ['bans', s, 'detail', b] as const,
  globalBans: (f: object) => ['globalBans', 'list', f] as const,
  globalBanCheck: (i: string) => ['globalBans', 'check', i] as const,
  whitelist: (s: string) => ['whitelist', s] as const,
  config: (s: string) => ['config', s] as const,
  configVersions: (s: string) => ['config', s, 'versions'] as const,
  configVersion: (s: string, v: number) => ['config', s, 'versions', v] as const,
  screenshots: (s: string, f: object) => ['screenshots', s, 'list', f] as const,
  commands: (s: string, f: object) => ['commands', s, 'list', f] as const,
  members: (s: string) => ['members', s] as const,
  invites: (s: string) => ['invites', s] as const,
  licenses: (s: string) => ['licenses', s] as const,
  webhooks: (s: string) => ['webhooks', s] as const,
  audit: (s: string, f: object) => ['audit', s, 'list', f] as const,
  lookup: (i: string) => ['lookup', i] as const,
} as const

/**
 * `staleTime` policy of PANEL.md §11, kept next to the keys so a new query
 * cannot silently inherit the global default when the table says otherwise.
 */
export const STALE = {
  default: 15_000,
  me: Infinity,
  config: 0,
  health: 10_000,
  summary: 60_000,
  slowList: 60_000,
  install: 300_000,
  lookup: 60_000,
  audit: 30_000,
  servers: 30_000,
} as const

export const REFETCH_INTERVAL = {
  /** WS fallback for the StatusStrip. */
  health: 30_000,
  /** Wizard step 3 polls harder because the first heartbeat can beat the socket. */
  waitingHeartbeat: 10_000,
} as const

export const GC_TIME = 300_000
